<script setup lang="ts">
import type { ReelItemProperty } from './types';
import type { PropertyRoomsInfo } from '~/components/VideoCard';
import type { HTMLAttributes } from 'vue';

import { cn } from '@/utils';
import { NuxtLink } from '#components';
import { useAgentProfileLink } from '~/composables/useAgentProfileNavigation';
import { contactModalChunkReady } from '~/composables/useVideoWarmup';
import {
	formatOpenHouseDate,
	getActiveOpenHouse
} from '~/core/entities/property';
import { computed, onMounted, ref } from 'vue';

import { BrokerAvatarInfo } from '~/components/BrokerAvatarInfo';
import { GlassButton } from '~/components/GlassButton';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import VideoProgressBar from '~/components/VideoCard/VideoProgressBar.vue';

import IconMaterialSymbolsDoorOpen from '~icons/material-symbols/door-open';
import IconMaterialSymbolsNorthEast from '~icons/material-symbols/north-east';
import IconPilarTriangle from '~icons/pilar/pilar-triangle';
import IconPilarWhatsapp from '~icons/pilar/whatsapp';

import ReelActionButtons from './ReelActionButtons.vue';

interface ReelPropertyInfoProps {
	property: ReelItemProperty;
	formattedPrice: string | null;
	formattedArea: string | null;
	roomsInfo: PropertyRoomsInfo | null;
	region: string;
	cityState: string;
	agentName: string;
	agentFirstName: string;
	companyName: string;
	brokerSrc: string;
	companySrc: string;
	isFavorite?: boolean;
	isFavoriteDataValid?: boolean;
	isFavoriteMainPulsing?: boolean;
	isFavoriteBackgroundPulsing?: boolean;
	isShareAnimating?: boolean;
	videoProgress?: number;
	isVideoLoaded?: boolean;
	duration?: number;
	showProgressBar?: boolean;
	showActionButtons?: boolean;
	/** Native share body on mobile reels (e.g. "Confira este imóvel por …"). */
	sharePromoText?: string;
	shortsIndex?: number;
	/** Extra classes for the action buttons container — use to control right offset per layout context. */
	actionButtonsClass?: HTMLAttributes['class'];
	class?: HTMLAttributes['class'];
}

const props = withDefaults(defineProps<ReelPropertyInfoProps>(), {
	isFavorite: false,
	isFavoriteDataValid: false,
	isFavoriteMainPulsing: false,
	isFavoriteBackgroundPulsing: false,
	isShareAnimating: false,
	videoProgress: 0,
	isVideoLoaded: false,
	duration: 0,
	showProgressBar: true,
	showActionButtons: true,
	sharePromoText: ''
});

const { agentProfileHref, handleAgentProfileClick } = useAgentProfileLink(
	() => props.property.agent,
	() => `/shorts/${props.property.commercialId}`
);

const emit = defineEmits<{
	(e: 'toggle-favorite'): void;
	(e: 'share'): void;
	(e: 'property-click'): void;
	(e: 'contact-click'): void;
	(e: 'seek', ratio: number): void;
}>();

const propertyLocationText = computed(() => {
	const type = props.property.propertyType?.name;
	const head =
		type && props.region ? `${type} em ${props.region}` : type || props.region;
	return [head, props.cityState].filter(Boolean).join(', ') || undefined;
});

// Clock read only on the client: SSR would compare against a stale (cached)
// time and render a different tag than hydration.
const now = ref<number | null>(null);
onMounted(() => {
	now.value = Date.now();
});

const openHouseDate = computed(() => {
	if (now.value === null) return null;
	const openHouse = getActiveOpenHouse(props.property.openHouses, now.value);
	return openHouse && formatOpenHouseDate(openHouse);
});
</script>

<template>
	<div
		:class="
			cn('absolute inset-x-0 bottom-0 z-20 flex flex-col gap-4', props.class)
		"
	>
		<div
			v-if="showActionButtons"
			:class="
				cn(
					'absolute -top-[18px] right-0 flex flex-col gap-3 lg:hidden',
					props.actionButtonsClass
				)
			"
		>
			<ReelActionButtons
				:property="property"
				:is-favorite="isFavorite"
				:is-favorite-data-valid="isFavoriteDataValid"
				:is-favorite-main-pulsing="isFavoriteMainPulsing"
				:is-favorite-background-pulsing="isFavoriteBackgroundPulsing"
				:is-share-animating="isShareAnimating"
				:share-promo-text="sharePromoText"
				:shorts-index="shortsIndex"
				@toggle-favorite="emit('toggle-favorite')"
				@share="emit('share')"
			/>
		</div>

		<component
			:is="agentProfileHref ? NuxtLink : 'button'"
			:type="agentProfileHref ? undefined : 'button'"
			:to="agentProfileHref || undefined"
			class="flex w-fit cursor-pointer items-center gap-2 text-left"
			@pointerdown.stop
			@click.stop="
				agentProfileHref ? handleAgentProfileClick() : emit('contact-click')
			"
		>
			<BrokerAvatarInfo
				class="gap-8 md:gap-9 overflow-hidden"
				avatar-shape="circle"
				avatar-class="size-9 md:size-11"
				sizes="linear"
				:broker-src="brokerSrc"
				:company-src="companySrc"
				:broker-name="agentName"
				:company-name="companyName"
				:info-classes="{
					wrapper: '[text-shadow:0_1px_3px_rgba(0,0,0,0.15)]',
					brokerName: 'text-sm md:text-base font-medium text-white',
					companyName: 'text-xs md:text-sm text-white'
				}"
			>
				<template #broker>{{ agentName }}</template>
				<template #company>{{ companyName }}</template>
			</BrokerAvatarInfo>
		</component>

		<div
			v-if="property.isExclusive || openHouseDate"
			data-testid="reel-property-tags"
			class="flex flex-wrap items-center gap-2 pr-14 lg:pr-0"
		>
			<span
				v-if="property.isExclusive"
				class="flex items-center gap-1.5 rounded-[2px] border border-white/10 bg-[rgba(102,102,102,0.3)] px-1.5 py-0.5 text-xs font-medium leading-[1.5] text-white backdrop-blur-[6px]"
			>
				<IconPilarTriangle class="size-3 shrink-0 fill-white" />
				Exclusivo
			</span>
			<span
				v-if="openHouseDate"
				class="flex items-center gap-1.5 rounded-[2px] border border-white/10 bg-[rgba(102,102,102,0.3)] px-1.5 py-0.5 text-xs font-medium leading-[1.5] text-white backdrop-blur-[6px]"
			>
				<IconMaterialSymbolsDoorOpen class="size-3.5 shrink-0" />
				Open House · {{ openHouseDate }}
			</span>
		</div>

		<div
			class="flex cursor-pointer items-center pr-14"
			@click.stop="emit('property-click')"
		>
			<div class="flex min-w-0 flex-1 flex-col gap-1">
				<h2
					v-if="formattedPrice"
					class="text-xl md:text-2xl font-medium text-white drop-shadow-sm"
				>
					{{ formattedPrice }}
				</h2>
				<div
					v-if="formattedArea || roomsInfo || property.parkingSpots"
					class="flex items-center gap-1.5 text-sm md:text-base text-white"
				>
					<h3 v-if="formattedArea" class="whitespace-nowrap">
						{{ formattedArea }}m²
					</h3>
					<span
						v-if="formattedArea && (roomsInfo || property.parkingSpots)"
						class="size-1 shrink-0 bg-white/20"
					/>
					<h3 v-if="roomsInfo" class="whitespace-nowrap">
						{{ roomsInfo.count }} {{ roomsInfo.label }}
					</h3>
					<span
						v-if="roomsInfo && property.parkingSpots"
						class="size-1 shrink-0 bg-white/20"
					/>
					<h3 v-if="property.parkingSpots" class="whitespace-nowrap">
						{{ property.parkingSpots }}
						{{ property.parkingSpots === 1 ? 'vaga' : 'vagas' }}
					</h3>
				</div>
				<h1
					v-if="propertyLocationText"
					class="truncate whitespace-nowrap text-sm text-white"
				>
					{{ propertyLocationText }}
				</h1>
			</div>
		</div>

		<div class="grid grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2 gap-3">
			<!-- Skeleton na dupla inteira: botao desabilitado parece indisponivel e
			     skeleton em so um dos dois fica torto. h-12 = altura final da linha. -->
			<template v-if="contactModalChunkReady">
				<Button
					variant="primary-dark"
					size="sm"
					as="button"
					class="min-w-0"
					:is-client-only="false"
					@click.stop="emit('contact-click')"
				>
					<IconPilarWhatsapp class="size-6 shrink-0" />
					<span class="truncate">Falar com {{ agentFirstName }}</span>
				</Button>
				<GlassButton
					shape="square"
					class="min-w-0 min-h-12 gap-2 px-4 text-sm font-medium text-white"
					@click.stop="emit('property-click')"
				>
					<span class="truncate">Ver detalhes</span>
					<IconMaterialSymbolsNorthEast class="size-5 shrink-0" />
				</GlassButton>
			</template>
			<template v-else>
				<Skeleton is-active class="h-12 w-full" />
				<Skeleton is-active class="h-12 w-full" variant="white" />
			</template>
		</div>

		<div
			v-if="showProgressBar && isVideoLoaded && duration > 0"
			data-progress-bar
		>
			<VideoProgressBar
				:progress="videoProgress"
				@seek="(ratio: number) => emit('seek', ratio)"
			/>
		</div>
	</div>
</template>
